import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';

// Crop the chosen square region from the source image to a `size`px JPEG blob.
async function cropToBlob(src, area, size = 512) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
  const canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, size, size);
  return new Promise(res => canvas.toBlob(b => res(b), 'image/jpeg', 0.9));
}

// Drag-to-position + zoom cropper for a profile photo. The crop is square (so it
// fits both the round avatar and the square tile); a live preview shows the tile.
export default function AvatarCropper({ src, onCancel, onSave }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);       // cropped area in source pixels
  const [natural, setNatural] = useState(null);  // source natural size
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const i = new Image();
    i.onload = () => setNatural({ w: i.naturalWidth, h: i.naturalHeight });
    i.src = src;
  }, [src]);

  const onComplete = useCallback((_, px) => setArea(px), []);

  const save = async () => {
    if (!area) return;
    setBusy(true);
    try { onSave(await cropToBlob(src, area)); }
    catch (e) { console.error('Crop failed:', e); setBusy(false); }
  };

  // Live square (tile) preview, computed with CSS so it updates instantly.
  const tilePreview = (() => {
    if (!area || !natural) return null;
    const P = 96;
    const scale = P / area.width;
    return {
      width: P, height: P, borderRadius: 8, flexShrink: 0,
      backgroundImage: `url(${src})`, backgroundRepeat: 'no-repeat',
      backgroundSize: `${natural.w * scale}px ${natural.h * scale}px`,
      backgroundPosition: `-${area.x * scale}px -${area.y * scale}px`,
    };
  })();

  return (
    <div className="modal-overlay" onClick={busy ? undefined : onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 700 }}>Position your photo</h3>
        <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 12px' }}>
          Drag to move, slider to zoom. The circle is your avatar; the square is how it looks on a tile.
        </p>

        <div style={{ position: 'relative', width: '100%', height: 280, background: 'var(--card-2)', borderRadius: 10, overflow: 'hidden' }}>
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onComplete}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 12 }}>
          <input
            type="range" min={1} max={3} step={0.01} value={zoom}
            onChange={e => setZoom(Number(e.target.value))}
            style={{ flex: 1 }}
            aria-label="Zoom"
          />
          {tilePreview && (
            <div style={{ textAlign: 'center' }}>
              <div style={tilePreview} />
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>On a tile</div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy || !area}>
            {busy ? 'Saving…' : 'Save photo'}
          </button>
        </div>
      </div>
    </div>
  );
}
